import type { ChatLine, Equipment, World } from '../game/world';
import type { Product, ShopTab } from '../game/constants/products';
import { equipmentFromSlots, type EquippedSlot } from '../account/account-client';
import { SvelteSet } from 'svelte/reactivity';
import { NOTIFICATION } from '../game/constants/hud';
import { lobbyRowsFrom, lobbyViewFrom } from '../lobby/from-wire';
import { features } from '../features/feature-state.svelte';
import type { LobbyListRow, LobbyView } from '../lobby/lobby-state';

const SHELL_REFRESH_MS = 100;

/** Pulls from `World` on its own timer rather than the render frame — rAF throttles to a standstill in a hidden tab, which would hide a purchase until the window is looked at again. */
export class SocketState {
  credits = $state(0);
  catalogueLoaded = $state(false);
  products = $state<readonly Product[]>([]);
  ownedProductIds = $state<ReadonlySet<number>>(new SvelteSet());
  equipment = $state<Equipment>({ decal: 0, trail: 0, tag: 0, shipSkins: [] });

  worldPopulations = $state<readonly number[]>([]);
  serverNum = $state(0);

  notification = $state('');
  shellVisible = $state(true);

  selfId = $state(-1);
  lobby = $state<LobbyView | null>(null);
  lobbyRows = $state<readonly LobbyListRow[]>([]);
  chat = $state<readonly ChatLine[]>([]);
  chatEnabled = $state(false);

  private featuresVersion = -1;
  private lobbyVersion = -1;
  private chatVersion = -1;
  private world: World | null = null;

  observe(world: World): void {
    if (this.world === null) {
      this.world = world;
      setInterval(() => {
        this.pull();
      }, SHELL_REFRESH_MS);
    }
    this.pull();
  }

  seed(fields: {
    credits?: number;
    owned?: readonly number[];
    equipped?: readonly EquippedSlot[];
  }): void {
    if (fields.credits !== undefined) this.credits = fields.credits;
    if (fields.owned !== undefined) this.ownedProductIds = new SvelteSet(fields.owned);
    if (fields.equipped !== undefined) this.equipment = equipmentFromSlots(fields.equipped);
  }

  notePurchase(productId: number, credit: number): void {
    this.credits = credit;
    const owned = new SvelteSet(this.ownedProductIds);
    owned.add(productId);
    this.ownedProductIds = owned;
    if (this.world !== null) {
      this.world.credits = credit;
      this.world.ownedProductIds.add(productId);
    }
  }

  owns(productId: number): boolean {
    return this.ownedProductIds.has(productId);
  }

  equipped(tab: ShopTab, shipId: number): number {
    switch (tab) {
      case 'decal':
        return this.equipment.decal;
      case 'trail':
        return this.equipment.trail;
      case 'tag':
        return this.equipment.tag;
      case 'skin':
        return this.equipment.shipSkins[shipId] ?? 0;
      case 'credits':
        return 0;
    }
  }

  private pull(): void {
    const world = this.world;
    if (world === null) return;

    const now = performance.now();
    const latest = world.notifications.at(-1);
    this.notification =
      latest !== undefined && now - latest.at < NOTIFICATION.lifetimeMs ? latest.text : '';

    this.serverNum = world.serverNum;
    this.worldPopulations = world.worldPopulations;
    this.selfId = world.selfId;

    if (this.featuresVersion !== world.featuresVersion && world.features !== null) {
      this.featuresVersion = world.featuresVersion;
      features.applyFromServer(world.features);
    }

    if (this.chatVersion !== world.chatVersion) {
      this.chatVersion = world.chatVersion;
      this.chat = [...world.chat];
    }
    this.chatEnabled = world.chatEnabled;

    if (this.lobbyVersion !== world.lobbyVersion) {
      this.lobbyVersion = world.lobbyVersion;
      this.lobby = world.lobby === null ? null : lobbyViewFrom(world.lobby);
      this.lobbyRows = lobbyRowsFrom(world.lobbies);
    }

    /** The socket wins only once it has spoken — an authenticated connection is not sent an `AccountSnapshot` until a purchase, so copying zeroes across before that would erase the HTTP seed. */
    if (world.accountStated) {
      this.credits = world.credits;
      if (!sameIds(this.ownedProductIds, world.ownedProductIds)) {
        this.ownedProductIds = new SvelteSet(world.ownedProductIds);
      }
    }
    if (world.equipmentStated) this.equipment = world.equipment;

    if (
      this.catalogueLoaded !== world.catalogueLoaded ||
      this.products.length !== world.products.size
    ) {
      this.catalogueLoaded = world.catalogueLoaded;
      this.products = [...world.products.values()];
    }
  }
}

function sameIds(a: ReadonlySet<number>, b: ReadonlySet<number>): boolean {
  if (a.size !== b.size) return false;
  for (const id of b) {
    if (!a.has(id)) return false;
  }
  return true;
}

export const socketState = new SocketState();
